import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import { Plus, User, Building, Trash2, Shield } from 'lucide-react'

interface Principal {
  id: string
  full_name: string
  legal_name: string | null
  email: string | null
  phone: string | null
  principal_kind: string
  country_code: string
  state_province: string | null
  city: string | null
  kyc_verified: boolean
}

type Props = {
  principals: Principal[]
  formation: { id: string; legal_name: string }
}

export default function PrincipalsIndex({ principals, formation }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()

  return (
    <DashboardLayout
      title="People"
      breadcrumbs={[
        { label: 'Formations', href: 'formations' },
        { label: formation.legal_name, href: `formations/${formation.id}` },
        { label: 'People' },
      ]}
    >
      <div>
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">People</h1>
              <p className="text-sm text-muted-foreground">Manage owners, directors, and officers for {formation.legal_name}</p>
            </div>
            <Button asChild size="sm">
              <Link href={`${basePath}/formations/${formation.id}/principals/new`}>
                <Plus className="w-4 h-4 mr-1.5" />
                Add Person
              </Link>
            </Button>
          </div>
        </div>

        {principals.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg py-12 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-6 h-6 text-muted-foreground" />
            </div>
            <h2 className="text-base font-medium mb-1">No people added yet</h2>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto text-sm">Add owners, directors, and officers to your formation</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/formations/${formation.id}/principals/new`}>
                <Plus className="w-4 h-4 mr-1.5" />
                Add First Person
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-0.5">
            {principals.map((principal) => (
              <div key={principal.id} className="group flex items-center justify-between py-2 px-3 -mx-3 rounded-lg hover:bg-muted transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center shrink-0">
                    {principal.principal_kind === 'entity' ? (
                      <Building className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <User className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{principal.full_name}</div>
                    {principal.legal_name && <div className="text-xs text-muted-foreground">{principal.legal_name}</div>}
                    {principal.email && <div className="text-xs text-muted-foreground">{principal.email}</div>}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {principal.city && principal.state_province
                          ? `${principal.city}, ${principal.state_province}`
                          : principal.country_code}
                      </span>
                      {principal.kyc_verified ? (
                        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 text-xs py-0">
                          <Shield className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-200 bg-yellow-50 text-xs py-0">Pending</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!principal.kyc_verified && (
                    <Button variant="ghost" size="sm" asChild className="h-7 text-xs text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50">
                      <Link href={`${basePath}/formations/${formation.id}/principals/${principal.id}/identity_verification`}>Verify Identity</Link>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={async () => { if (await confirm({ description: 'Remove this person?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/formations/${formation.id}/principals/${principal.id}`) }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
